import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { searchMessages } from '../utils/search';
import { formatMessageId } from '../utils/secsDataLoader';
import BrandLogo from './BrandLogo';

export default function Header() {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode, setSearchQuery, toggleSidebar } = useAppStore();
  const [localQuery, setLocalQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const results = localQuery.length >= 2 ? searchMessages(localQuery).slice(0, 5) : [];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (localQuery.trim()) {
      setSearchQuery(localQuery);
      navigate('/search');
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (messageId: string) => {
    navigate(`/message/${messageId}`);
    setLocalQuery('');
    setShowSuggestions(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)] shadow-sm">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-4">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-[var(--color-bg-tertiary)] transition-colors"
            aria-label="Toggle sidebar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 shrink-0 rounded-lg bg-white shadow-sm ring-1 ring-black/5 flex items-center justify-center overflow-hidden">
              <BrandLogo variant="symbol" className="w-6 h-6 object-contain" />
            </div>
            <div>
              <h1 className="text-lg font-bold leading-tight">SECS-II Reference</h1>
              <p className="text-xs text-[var(--color-text-tertiary)]">SEMI E5 · Gather System</p>
            </div>
          </div>
        </div>

        <div className="flex-1 max-w-2xl mx-8 relative" ref={searchRef}>
          <form onSubmit={handleSearch}>
            <div className="relative">
              <input
                type="text"
                value={localQuery}
                onChange={(e) => {
                  setLocalQuery(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Search messages, functions, or data items..."
                className="input pl-10 pr-4"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-text-tertiary)]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </form>

          {showSuggestions && results.length > 0 && (
            <div className="absolute top-full mt-2 w-full bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg shadow-lg overflow-hidden">
              {results.map((result) => {
                const messageId = formatMessageId(result.item.stream, result.item.function);
                return (
                  <button
                    key={messageId}
                    onClick={() => handleSuggestionClick(messageId)}
                    className="w-full px-4 py-3 text-left hover:bg-[var(--color-bg-secondary)] transition-colors border-b border-[var(--color-border)] last:border-b-0"
                  >
                    <div className="font-medium text-sm">{messageId} - {result.item.name}</div>
                    <div className="text-xs text-[var(--color-text-secondary)] mt-1 line-clamp-1">
                      {result.item.description}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/data-types')}
            className="p-2 rounded-lg hover:bg-[var(--color-bg-tertiary)] transition-colors"
            title="Data Types Reference"
            aria-label="Data Types Reference"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
            </svg>
          </button>

          <button
            onClick={() => navigate('/data-items')}
            className="p-2 rounded-lg hover:bg-[var(--color-bg-tertiary)] transition-colors"
            title="Data Item Dictionary"
            aria-label="Data Item Dictionary"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16M16 12h4v4h-4z" />
            </svg>
          </button>

          <button
            onClick={() => navigate('/gem')}
            className="p-2 rounded-lg hover:bg-[var(--color-bg-tertiary)] transition-colors"
            title="GEM Capabilities"
            aria-label="GEM Capabilities"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </button>

          <button
            onClick={toggleDarkMode}
            className="p-2 rounded-lg hover:bg-[var(--color-bg-tertiary)] transition-colors"
            title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
          >
            {isDarkMode ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>

        </div>
      </div>
    </header>
  );
}
